import { useContext, useState, useEffect, useCallback, FC } from 'react'
import qs from 'query-string'
import { useDispatch } from 'react-redux'
import Router from 'next/router'
import NoSSR from 'react-no-ssr'

import { logger, setUserAccessCookies, cmsPageUrl } from '@utils'
import { Modal, Button } from '@mch-group/uikit-components'
import { LoginContext } from '@providers/Login'
import { useLabelsFromAPI } from '@dmi-mch/hooks'

import { Events as EventService } from '../../services'
// import FullScreenLoader from '../FullScreenLoader'
import Text from '../Text'
import { signOut } from '../../stores/user'
import { getSiteLanguage } from '../../utils/storage'
import { formatEventLink } from '../../utils/events'

const MessageModal = props => (
  // @ts-ignore
  <Modal
    show={props.open}
    size='md'
    onHide={props.handleCloseModal}
  >
    <Modal.Header title={props.title} subline='' onHide={props.handleCloseModal} />
    <Modal.Body>
      <Text isHtml>
        {props.message}
      </Text>
      <Button type='button' className='mt-10' onClick={props.handleCloseModal}>
        {props.okButton}
      </Button>
    </Modal.Body>
  </Modal>
)

const OneClickRSVP: FC = () => {
  const [message, setMessage] = useState('')
  const params = qs.parse(window.location.search)
  const action = params.action
  const invitationId = params.i

  // Different API calls depending on URL param. Let's default it to this.action = 'eventdetails'
  // and overwrite if needed
  let rsvpCall = EventService.viewRSVPOneClick
  const { labels: apiLabels = {} } = useLabelsFromAPI(['RSVPAttendance'], getSiteLanguage())

  let okMessage = ''
  if (action === 'acceptinvitation') {
    rsvpCall = EventService.postRSVPOneClick
    okMessage = apiLabels?.RSVPAttendance?.rsvpAcceptedMessage
  } else if (action === 'declineinvitation') {
    rsvpCall = EventService.delRSVPOneClick
    okMessage = apiLabels?.RSVPAttendance?.rsvpDeclinedMessage
  }
  let okButton = apiLabels?.RSVPAttendance?.rsvpConfirmButtonLabel
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false)
  const [isReady, setIsReady] = useState(false)

  const [, setIsLoading] = useState(true)
  const [invitationResponse, setInvitationResponse] = useState(null)
  const { showTCModal, user } = useContext(LoginContext)

  const dispatch = useDispatch()

  // When closing RSVP message modal, show TC modal or we're all done. Decide here.
  const redirectToEventDetail = useCallback(async (invitationResponseData) => {
    const tcAccepted = invitationResponseData?.data?.sessionSummary?.termsAndConditionsAccepted
    setUserAccessCookies(invitationResponseData)
    // If TC is already accepted, redirect to detail
    if (tcAccepted) {
      // Set the cookies to log in the new user. Likely is the same user.
      setIsInvitationModalOpen(false)
      setIsLoading(false)
      const eventID = parseInt(invitationResponseData?.data?.rsvpUserInfo?.subEvent?.cmsId, 0)
      try {
        const [event]: any = await Promise.all([
          EventService.getEventById(eventID, getSiteLanguage())
        ])
        if (event.ok) {
          Router.push(
            cmsPageUrl(event?.data?.id, '/', {
              opentime: event?.data?.openingDateTimes && event.data?.openingDateTimes[0]?.id
            }),
            formatEventLink(event?.data?.id, event?.data?.cleanTitle,
              event?.data?.openingDateTimes.length > 0 && event?.data?.openingDateTimes[0]?.id)
          )
        }
      } catch (e) {
        logger(e)
      }
    } else {
      // If TC is not accepted, stay in the same page, requesting to accept the TC
      setIsInvitationModalOpen(false)
      showTCModal({
        onTCAcceptedCallback: async () => {
          const [event] = await Promise.all([EventService
            .getEventById(
              parseInt(invitationResponseData?.data?.rsvpUserInfo?.subEvent?.cmsId, 0),
              getSiteLanguage()
            )])
          Router.push(
            // @ts-ignore
            cmsPageUrl(event?.data?.id, '/', {
              // @ts-ignore
              opentime: event?.data?.openingDateTimes && event.data?.openingDateTimes[0]?.id
            }),
            // @ts-ignore
            formatEventLink(event?.data?.id, event?.data?.cleanTitle, event?.data?.openingDateTimes[0]?.id)
          )
        }
      })
    }
  }, [showTCModal])

  const handleInvitation = useCallback(async () => {
    setIsReady(true)
    if (Object.keys(params).length > 0) {
      if (user?.accessToken) { dispatch(signOut()) }
      try {
        let outputMessage = ''
        const response = await rsvpCall(params)
        // @ts-ignore
        if (response.ok && 'loginResponse' in response.data) {
          outputMessage = okMessage
          // Duplicate response
          const newResponse = { ...response }
          // Create new Data to ammend BE unconsistency (they added new node loginResponse)
          // @ts-ignore
          const newData = { ...newResponse.data, ...response.data.loginResponse }
          // Delete unnecessary and confusing nodes
          delete newData.loginResponse
          // Append
          newResponse.data = newData

          // Case 1: If action is just for visit event, Redirect to TC (in case don't have) + Event Detail
          // @ts-ignore
          setInvitationResponse(newResponse)
          setIsLoading(false)
          if (action === 'eventdetails') {
            redirectToEventDetail(newResponse)
          } else {
            // Case 2: Show accept/decline RSVP modal
            setMessage(outputMessage)
            setIsInvitationModalOpen(response.ok)
          }
        } else {
          setIsLoading(false)
        }
      } catch (e) { logger(e) }
    }
  }, [action, dispatch, okMessage, params, redirectToEventDetail, rsvpCall, user])

  useEffect(() => {
    if (params?.action && !isReady && apiLabels.RSVPAttendance && !invitationResponse) {
      handleInvitation()
    }
  }, [apiLabels.RSVPAttendance, handleInvitation, invitationResponse, isReady, params])

  if (!invitationId) return null

  return (
    <>
      <MessageModal
        title={apiLabels?.RSVPAttendance?.rsvpPopupTitleLabel}
        message={message}
        okButton={okButton}
        open={isInvitationModalOpen}
        handleCloseModal={() => { redirectToEventDetail(invitationResponse) }}
      />
      {/* <FullScreenLoader active={isLoading} /> */}
    </>
  )
}



const OneClickRSVPNoSSR = (props) => <NoSSR><OneClickRSVP {...props} /></NoSSR>

export default (OneClickRSVPNoSSR)